import styles from './sidebar.module.css'
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { MenuItem } from '../../state/types'
import { useUIHooks } from '../../hooks/uiHooks'

type Props = {
    menu: MenuItem[]
}

export const Sidebar = (props: Props) => {
    const { menu } = props
    const [searchTerm, setSearchTerm] = useState("");
    const { getFilteredMenu } = useUIHooks();
    
    const menuToRender = getFilteredMenu(searchTerm);
    const isSearching = useRef(false);

    const [isHovered, setIsHovered] = useState<boolean>(false)
    const [openedItemLevel1, setOpenedItemLevel1] = useState<string[]>([])
    const [openedItemLevel2, setOpenedItemLevel2] = useState<string[]>([])
    const [openedItemLevel3, setOpenedItemLevel3] = useState<string[]>([])
    const [activeItem, setActiveItem] = useState<string>()

    const navigate = useNavigate()

    const normalizeText = (text: string) => 
        text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

    useEffect(() => {
        if (searchTerm.trim().length > 0 && menuToRender.length > 0 && isSearching.current) {
            const l1: string[] = [];
            const l2: string[] = [];
            const l3: string[] = [];

            const checkMatch = (item: MenuItem, term: string): boolean => {
                const normalizedItem = normalizeText(item.label);
                const normalizedTerm = normalizeText(term);
                
                if (normalizedItem.includes(normalizedTerm)) return true;
                if (item.children) {
                    return item.children.some(child => checkMatch(child, term));
                }
                return false;
            };

            const findOpenedLevels = (items: MenuItem[], level: number) => {
                items.forEach(item => {
                    if (item.children && item.children.length > 0) {
                        // Verificamos si hay un match (sin tildes) en los descendientes
                        const hasMatchInside = item.children.some(child => checkMatch(child, searchTerm));
                        
                        if (hasMatchInside) {
                            if (level === 1) l1.push(item.id);
                            if (level === 2) l2.push(item.id);
                            if (level === 3) l3.push(item.id);
                            findOpenedLevels(item.children, level + 1);
                        }
                    }
                });
            };

            findOpenedLevels(menuToRender, 1);
            setOpenedItemLevel1(l1);
            setOpenedItemLevel2(l2);
            setOpenedItemLevel3(l3);
            
            isSearching.current = false;
        }
    }, [searchTerm, menuToRender]);

    const isOpened = (id: string, level: number) => {
        if (level === 1) return openedItemLevel1.includes(id);
        if (level === 2) return openedItemLevel2.includes(id);
        return openedItemLevel3.includes(id);
    };

    const toggleItem = (id: string, level: number) => {
        isSearching.current = false; 
        const setFn = level === 1 ? setOpenedItemLevel1 : (level === 2 ? setOpenedItemLevel2 : setOpenedItemLevel3);
        setFn(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const handleSearchChange = (val: string) => {
        isSearching.current = true;
        setSearchTerm(val);
    };

    const HighlightedText = ({ text, highlight }: { text: string, highlight: string }) => {
        if (!highlight.trim()) return <span>{text}</span>;
        
        const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        
        // Esta función crea un patrón que acepta tanto la letra con tilde como sin ella
        const makeAccentInsensitive = (str: string) => {
            return str
                .replace(/a/gi, '[a,á,à,ä,â]')
                .replace(/e/gi, '[e,é,è,ë,ê]')
                .replace(/i/gi, '[i,í,ì,ï,î]')
                .replace(/o/gi, '[o,ó,ò,ö,ô]')
                .replace(/u/gi, '[u,ú,ù,ü,û]');
        };

        const regex = new RegExp(`(${makeAccentInsensitive(escapeRegExp(highlight))})`, 'gi');
        const parts = text.split(regex);
        return (
            <span>
                {parts.map((part, i) => 
                    regex.test(part) 
                        ? <b key={i} className={styles.highlight}>{part}</b> 
                        : part
                )}
            </span>
        );
    };
    
    const hover = () => setIsHovered(true)
    const unhover = () => setIsHovered(false)

    const onClickItem = (item: MenuItem, level: number) => () => {
        if (item.path) {
            navigate(item.path);
            setActiveItem(item.id);
        } else if (item.children) {
            toggleItem(item.id, level);
        }
    };

    const renderItem = (item: MenuItem, level: number) => {
        const opened = isOpened(item.id, level);
        const openedClassName = opened ? styles.opened : ''
        const activeClassName = !item.children && activeItem === item.id ? styles.active : ''
        const visibleClassName = isHovered ? styles.visible : ''

        return (
            <div key={item.id} className={`${styles.item} ${openedClassName}`}>
                <div 
                    className={`${styles.titleContainer} ${openedClassName} ${activeClassName}`} 
                    onClick={onClickItem(item, level)}
                >
                    <span>
                        <i className={`bi bi-${item.icon || 'record-fill'} ${styles.icon}`} />
                        <span className={`${styles.title} ${visibleClassName}`}>
                            <HighlightedText text={item.label} highlight={searchTerm} />
                        </span>
                    </span>
                    {item.children && <i className={`bi bi-chevron-down ${styles.arrow} ${openedClassName}`} />}
                </div>

                {isHovered && item.children && (
                    <div className={`${styles.content} ${openedClassName}`}>
                        {item.children.map((child: MenuItem) => renderItem(child, level + 1))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <aside
            onMouseEnter={hover}
            onMouseLeave={unhover}
            className={`${styles.container} ${isHovered ? styles.hover : ''}`}
        >
            <div className={styles.searchContainer}>
                <div className={styles.inputWrapper}>
                    <i className={`bi bi-search ${styles.searchIcon}`} />
                    <input 
                        type="text"
                        className={`form-control form-control-sm ${styles.searchInput} ${!isHovered ? styles.hidden : ''}`}
                        placeholder="Buscar..."
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                    />
                    {searchTerm && isHovered && (
                        <i 
                            className={`bi bi-x-circle-fill ${styles.clearIcon}`} 
                            onClick={() => handleSearchChange("")}
                        />
                    )}
                </div>
            </div>
            {menuToRender.map((item: MenuItem) => renderItem(item, 1))}
        </aside>
    )
}
